/**
 * Notification center for completed N8N drafts (and any other completion events).
 *
 * How it works:
 *  - We store a small array of notifications in localStorage (per user) so they
 *    persist across page reloads.
 *  - A polling query against the `documents` table watches for documents whose
 *    status has flipped from `generating` -> `draft` (i.e. the workflow returned
 *    its draft and someone — N8N or the edge function — saved it back).
 *  - When a transition is detected we add a notification + fire a sonner toast.
 *
 * The provider is mounted once at the app root and exposes:
 *    - notifications, unreadCount
 *    - markAllRead(), markRead(id), clearAll()
 *    - addNotification(...)  // manual add (used elsewhere if needed)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export type AppNotification = {
  id: string;
  documentId?: string;
  title: string;
  body: string;
  href?: string;
  createdAt: string;
  read: boolean;
};

type NotificationsContextValue = {
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clearAll: () => void;
  addNotification: (n: Omit<AppNotification, "id" | "createdAt" | "read">) => void;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const MAX_NOTIFICATIONS = 30;
const storageKey = (uid: string | undefined) => `casey:notifications:${uid ?? "anon"}`;

const loadFromStorage = (uid: string | undefined): AppNotification[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.slice(0, MAX_NOTIFICATIONS);
    return [];
  } catch {
    return [];
  }
};

const saveToStorage = (uid: string | undefined, list: AppNotification[]) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(storageKey(uid), JSON.stringify(list.slice(0, MAX_NOTIFICATIONS)));
  } catch {
    /* quota — ignore */
  }
};

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>(() => loadFromStorage(user?.id));

  // Reload when the auth user changes.
  useEffect(() => {
    setNotifications(loadFromStorage(user?.id));
  }, [user?.id]);

  // Persist on change.
  useEffect(() => {
    saveToStorage(user?.id, notifications);
  }, [user?.id, notifications]);

  const addNotification = useCallback(
    (n: Omit<AppNotification, "id" | "createdAt" | "read">) => {
      setNotifications((prev) => {
        // De-dupe by documentId — never add two notifications for the same doc.
        if (n.documentId && prev.some((p) => p.documentId === n.documentId)) return prev;
        const next: AppNotification = {
          ...n,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          read: false,
        };
        return [next, ...prev].slice(0, MAX_NOTIFICATIONS);
      });
    },
    [],
  );

  const markAllRead = useCallback(
    () => setNotifications((prev) => prev.map((n) => ({ ...n, read: true }))),
    [],
  );
  const markRead = useCallback(
    (id: string) =>
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n))),
    [],
  );
  const clearAll = useCallback(() => setNotifications([]), []);

  /* --------------------------------------------------------------------- */
  /* Poll for completed drafts                                              */
  /* --------------------------------------------------------------------- */

  // Track which docs we've already notified about within this session,
  // so a temporary localStorage clear doesn't re-toast every poll.
  const notifiedRef = useRef<Set<string>>(new Set());

  // Seed the dedupe set from existing notifications on mount.
  useEffect(() => {
    notifications.forEach((n) => {
      if (n.documentId) notifiedRef.current.add(n.documentId);
    });
    // Intentionally only on user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Poll every 8s for documents that have been generated recently.
  useQuery({
    queryKey: ["notifications-watch", user?.id],
    enabled: !!user,
    refetchInterval: 8000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      // Find recently-completed drafts (text now present, status is draft/final,
      // updated in the last hour) — this catches both the edge function path
      // and any future N8N callback path.
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("documents")
        .select("id, title, tool, status, generated_at, updated_at")
        .not("generated_text", "is", null)
        .gte("updated_at", oneHourAgo)
        .order("updated_at", { ascending: false })
        .limit(20);
      if (error) throw error;

      (data ?? []).forEach((row) => {
        if (notifiedRef.current.has(row.id)) return;
        notifiedRef.current.add(row.id);

        const message = `Draft ready: ${row.title}`;
        addNotification({
          documentId: row.id,
          title: "Draft ready",
          body: row.title,
          href: `/documents/${row.id}`,
        });

        // Only toast for genuinely fresh completions (last 2 minutes) so
        // we don't yell on first login.
        const ts = new Date(row.generated_at ?? row.updated_at).getTime();
        if (Date.now() - ts < 2 * 60 * 1000) {
          toast.success(message, {
            description: "Open it now to review, edit or download.",
            action: {
              label: "Open",
              onClick: () => {
                window.location.assign(`/documents/${row.id}`);
              },
            },
          });
        }
      });

      return data;
    },
  });

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount: notifications.filter((n) => !n.read).length,
      markAllRead,
      markRead,
      clearAll,
      addNotification,
    }),
    [notifications, markAllRead, markRead, clearAll, addNotification],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    // Safe fallback — lets components render even without provider in tests.
    return {
      notifications: [],
      unreadCount: 0,
      markAllRead: () => {},
      markRead: () => {},
      clearAll: () => {},
      addNotification: () => {},
    } as NotificationsContextValue;
  }
  return ctx;
};
